import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { fetchVehicleTypes } from "../../api/vehicletypeApi";
import CarList from "../CarModelList";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

const predefinedColors = [
  { name: "Black", value: "#000000" },
  { name: "Jet Black", value: "#343434" },
  { name: "White", value: "#FFFFFF" },
  { name: "Pearl White", value: "#FDFDFD" },
  { name: "Silver", value: "#C0C0C0" },
  { name: "Metallic Silver", value: "#A9A9A9" },
  { name: "Gray", value: "#808080" },
  { name: "Charcoal Gray", value: "#36454F" },
  { name: "Gunmetal", value: "#2A3439" },
  { name: "Steel Blue", value: "#4682B4" },
  { name: "Midnight Blue", value: "#191970" },
  { name: "Navy Blue", value: "#000080" },
  { name: "Deep Blue", value: "#00008B" },
  { name: "Sky Blue", value: "#87CEEB" },
  { name: "Electric Blue", value: "#7DF9FF" },
  { name: "Red", value: "#FF0000" },
  { name: "Crimson Red", value: "#DC143C" },
  { name: "Maroon", value: "#800000" },
  { name: "Burgundy", value: "#800020" },
  { name: "Candy Apple Red", value: "#D1001C" },
  { name: "Orange", value: "#FFA500" },
  { name: "Burnt Orange", value: "#CC5500" },
  { name: "Gold", value: "#FFD700" },
  { name: "Champagne", value: "#EFEBD6" },
  { name: "Beige", value: "#F5F5DC" },
  { name: "Brown", value: "#8B4513" },
  { name: "Coffee Brown", value: "#4B3621" },
  { name: "Yellow", value: "#FFFF00" },
  { name: "Lemon Yellow", value: "#FFF44F" },
  { name: "Green", value: "#008000" },
  { name: "British Racing Green", value: "#004225" },
  { name: "Olive", value: "#808000" },
  { name: "Teal", value: "#008080" },
  { name: "Mint Green", value: "#98FF98" },
  { name: "Turquoise", value: "#40E0D0" },
  { name: "Purple", value: "#800080" },
  { name: "Plum", value: "#8E4585" },
  { name: "Pink", value: "#FFC0CB" },
  { name: "Rose Gold", value: "#B76E79" },
  { name: "Copper", value: "#B87333" },
  { name: "Bronze", value: "#CD7F32" },
  { name: "Titanium", value: "#878681" },
  { name: "Ivory", value: "#FFFFF0" },
  { name: "Sand", value: "#C2B280" }
];


const CreateVehicleModal = ({ onClose, onSubmit, clientId, vehicleTypes, editVehicle }) => {
  const { register, handleSubmit, reset, setValue, watch } = useForm();
  const [vehicletype, setvehicletype] = useState([]);
  const { t } = useTranslation();
  const [colorDropdownOpen, setColorDropdownOpen] = useState(false);

  useEffect(() => {
    async function getvehicletype() {
      const data = await fetchVehicleTypes({});
      setvehicletype(data);
      setValue("vehicle_type_id", editVehicle?.vehicle_type_id);
    }

    getvehicletype();

    if (editVehicle) {
      setValue("vehicle_type_id", editVehicle?.vehicle_type_id);
      setValue("registration", editVehicle?.registration);
      setValue("plate_number", editVehicle?.plate_number);
      setValue("color", editVehicle?.color);
      setValue("model", editVehicle?.model);
      setValue("year_made", editVehicle?.year_made);
    }
  }, [editVehicle]);

  const handleFormSubmit = (data) => {
    const processedData = {
      ...data,
      client_id: clientId,
      vehicle_id: editVehicle?.vehicle_id,
      date_created: new Date(),
    };
    onSubmit(processedData);
    onClose();
  };

  const handleCarModel = (data) => {
    setValue("model", data);
  };

  const selectedColor = predefinedColors.find(c => c.value === watch("color"));

  return (
    <div dir={i18next.language === "ar" ? "rtl" : "ltr"} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800">{t("addvehicle")}</h2>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 text-gray-800">

            {/* Vehicle Type */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('selectvehicletype')}</label>
              <select
                {...register("vehicle_type_id", { required: true })}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">{t('selectvehicletype')}</option>
                {vehicletype.map((type) => (
                  <option key={type.vehicle_type_id} value={type.vehicle_type_id}>
                    {type.name_en}
                  </option>
                ))}
              </select>
            </div>

            {/* Registration */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('registration')}</label>
              <select
                {...register("registration", { required: true })}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="PRIVATE">{t('private')}</option>
                <option value="TAXI">{t('taxi')}</option>
                <option value="LIMO">{t('limo')}</option>
                <option value="TRANSPORT">{t('publicTransport')}</option>
                <option value="BIKE">{t('bike')}</option>
                <option value="OTHER">{t('other')}</option>
              </select>
            </div>

            {/* Plate Number */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('platenumber')}</label>
              <input
                {...register("plate_number", { required: true })}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Color Selection (Custom Dropdown with Color Block) */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1">{t('color')}</label>
              <button
                type="button"
                onClick={() => setColorDropdownOpen(!colorDropdownOpen)}
                className="w-full flex items-center justify-between p-2 border border-gray-300 rounded-md"
              >
                {selectedColor ? (
                  <div className="flex items-center gap-2">
                    <span
                      className="w-5 h-5 rounded-full border"
                      style={{ backgroundColor: selectedColor.value }}
                    />
                    {selectedColor.name}
                  </div>
                ) : (
                  <span className="text-gray-500">{t('select')}</span>
                )}
                <span className="text-gray-400">▼</span>
              </button>

              {colorDropdownOpen && (
                <ul className="absolute z-10 bg-white border border-gray-300 rounded-md mt-1 w-full max-h-48 overflow-y-auto shadow">
                  {predefinedColors.map((color) => (
                    <li
                      key={color.value}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setValue("color", color.value);
                        setColorDropdownOpen(false);
                      }}
                    >
                      <span
                        className="w-5 h-5 rounded-full border"
                        style={{ backgroundColor: color.value }}
                      />
                      <span>{color.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Year Made */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('yearmade')}</label>
              <input
                {...register("year_made", {
                  valueAsNumber: true,
                  validate: (value) =>
                    !value || (value >= 1900 && value <= new Date().getFullYear()),
                })}
                placeholder="e.g. 2021"
                type="number"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            {/* Model (readonly) */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('model')}</label>
              <input
                {...register("model")}
                className="w-full p-2 border border-gray-300 rounded-md"
                disabled
              />
            </div>

            <CarList onCarSelect={handleCarModel} />



            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-orange-400 text-white rounded-xl hover:bg-orange-700"
              >
                {editVehicle ? t('update') : t('create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateVehicleModal;
